import { IconoirProvider } from 'iconoir-react';
import React, { Suspense } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { APP_NAMES } from '@savgroup-front-common/constants';
import { ToastContainer } from '@savgroup-front-common/core/atoms/Toast';
import { TooltipProvider } from '@savgroup-front-common/core/atoms/Tooltip/Tooltip';
import { ConfirmationContextProvider } from '@savgroup-front-common/core/components/Confirmation/ConfirmationContextProvider';
import { GraphqlProvider } from '@savgroup-front-common/core/components/GraphqlProvider/GraphqlProvider';
import { GraphQlSseContextProvider } from '@savgroup-front-common/core/components/GraphqlSseContext/GraphqlSseContext';
import { HotjarProvider } from '@savgroup-front-common/core/components/HotjarProvider';
import IntlManager from '@savgroup-front-common/core/components/IntlManager/IntlManager';
import { NewUiProvider } from '@savgroup-front-common/core/components/NewUiProvider/NewUiProvider';
import { retryImportChunk } from '@savgroup-front-common/core/helpers/retryImportChunk';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { AppErrorBoundary } from '@savgroup-front-common/core/molecules/ErrorBoundary';
import { NetworkStatusProvider } from '@savgroup-front-common/core/molecules/NetworkStatusProvider/NetworkStatusProvider';
import { ToastsProvider } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { ExternalPortal } from '@savgroup-front-common/core/protons/ExternalPortal/ExternalPortal';
import { KeyboardLayoutProvider } from '@savgroup-front-common/core/protons/KeyboardLayoutProvider';

import HOTJAR_CONFIG from '@/configuration/hotjar';
import { getIsBffFeatureEnabled } from '@/helpers/getIsBffFeatureEnabled';

import { logCritical } from '../../configuration';

import { store } from './App.config';
import { AppContextProvider } from './App.context';
import DebugLayout from './DebugLayout';
import GlobalStyleManager from './GlobalStyleManager';
import { useInitGoogleMap } from '@savgroup-front-common/core/hooks';
import CheckAuthorized from './login/CheckAuthorized';
import { Invitation } from './login/Invitation/Invitation';
import { OAuthCallback } from './login/OAuthCallback/OAuthCallback';
import { ThemeProvider } from './ThemeProvider';

const QrcodeUploadContainer = React.lazy(() =>
  retryImportChunk(
    () =>
      import('@savgroup-front-common/core/components/QrcodeUpload/QrcodeUploadContainer'),
  ),
);

const shouldEnableReactQueryDevtools =
  import.meta.env.VITE_ENABLE_REACT_QUERY_DEVTOOLS === 'true';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 1,
    },
  },
});

const App = () => {
  useInitGoogleMap();

  const isBffFeatureEnabled = getIsBffFeatureEnabled();

  try {
    return (
      <IconoirProvider
        iconProps={{
          color: 'currentColor',
          width: '1.25rem',
          height: '1.25rem',
        }}
      >
        <HotjarProvider config={HOTJAR_CONFIG}>
          <ToastsProvider>
            <NetworkStatusProvider>
              <ReduxProvider store={store}>
                <KeyboardLayoutProvider>
                  <NewUiProvider>
                    <AppErrorBoundary>
                      <AppContextProvider>
                        <ThemeProvider>
                          <IntlManager appName={APP_NAMES.CONTROL}>
                            <GraphQlSseContextProvider>
                              <GraphqlProvider>
                                <ConfirmationContextProvider>
                                  <TooltipProvider>
                                    <>
                                      <GlobalStyleManager />
                                      <QueryClientProvider client={queryClient}>
                                        {shouldEnableReactQueryDevtools && (
                                          <ReactQueryDevtools
                                            initialIsOpen={false}
                                          />
                                        )}

                                        <ExternalPortal>
                                          <ToastContainer topOffset="6rem" />
                                        </ExternalPortal>

                                        <BrowserRouter>
                                          <Routes>
                                            <Route
                                              path="/*"
                                              element={
                                                <Suspense
                                                  fallback={<BaseLoader />}
                                                >
                                                  <DebugLayout />
                                                </Suspense>
                                              }
                                            >
                                              <Route
                                                path="invitation/*"
                                                element={
                                                  <Suspense
                                                    fallback={<BaseLoader />}
                                                  >
                                                    <Invitation />
                                                  </Suspense>
                                                }
                                              />

                                              <Route
                                                path="qrcodeUpload/*"
                                                element={
                                                  <Suspense
                                                    fallback={<BaseLoader />}
                                                  >
                                                    <QrcodeUploadContainer />
                                                  </Suspense>
                                                }
                                              />

                                              {!isBffFeatureEnabled && (
                                                <Route
                                                  path="callback"
                                                  element={
                                                    <Suspense
                                                      fallback={<BaseLoader />}
                                                    >
                                                      <OAuthCallback />
                                                    </Suspense>
                                                  }
                                                />
                                              )}

                                              <Route
                                                path="*"
                                                element={
                                                  <Suspense
                                                    fallback={<BaseLoader />}
                                                  >
                                                    <CheckAuthorized />
                                                  </Suspense>
                                                }
                                              />
                                            </Route>
                                          </Routes>
                                        </BrowserRouter>
                                      </QueryClientProvider>
                                    </>
                                  </TooltipProvider>
                                </ConfirmationContextProvider>
                              </GraphqlProvider>
                            </GraphQlSseContextProvider>
                          </IntlManager>
                        </ThemeProvider>
                      </AppContextProvider>
                    </AppErrorBoundary>
                  </NewUiProvider>
                </KeyboardLayoutProvider>
              </ReduxProvider>
            </NetworkStatusProvider>
          </ToastsProvider>
        </HotjarProvider>
      </IconoirProvider>
    );
  } catch (err) {
    logCritical(err);
    throw err;
  }
};

export default App;
